import { ActionTabs, DEFAULT_CONFIG, ImageCard, MeshGradient } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import type {
  ApplicationPlan,
  UserCollege,
  UserCollegeStatus,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { awardLetterAction } from './AwardLetterButton';
import { NetCostText } from './NetCostText';
import { PlanPicker } from './PlanPicker';
import { RequirementTags } from './RequirementTags';
import { StatusChip } from './StatusChip';
import { TierChip } from './TierChip';
import { canOpenAwardLetter, deadlineText, groupByTier, hasDeadline } from '../myColleges.util';

const STRINGS = appStrings.modules.myColleges;

type MyCollegesCardsProps = Readonly<{
  colleges: readonly UserCollege[];
  hasFamilyIncome: boolean;
  onStatusChange: (college: UserCollege, status: UserCollegeStatus) => void;
  onPlanChange: (college: UserCollege, plan: ApplicationPlan) => void;
  onRemove: (college: UserCollege) => void;
}>;

export const MyCollegesCards = ({
  colleges,
  hasFamilyIncome,
  onStatusChange,
  onPlanChange,
  onRemove,
}: MyCollegesCardsProps) => (
  <Box sx={groupsSx}>
    {groupByTier(colleges).map(({ tier, colleges: group }) => (
      <Box key={tier ?? 'not-rated'} sx={groupSx}>
        <Box>
          <TierChip tier={tier} count={group.length} />
        </Box>
        <Box sx={gridSx}>
          {group.map((college) => (
            <MyCollegeCard
              key={college.id}
              college={college}
              hasFamilyIncome={hasFamilyIncome}
              onStatusChange={onStatusChange}
              onPlanChange={onPlanChange}
              onRemove={onRemove}
            />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

type MyCollegeCardProps = Readonly<{
  college: UserCollege;
  hasFamilyIncome: boolean;
  onStatusChange: (college: UserCollege, status: UserCollegeStatus) => void;
  onPlanChange: (college: UserCollege, plan: ApplicationPlan) => void;
  onRemove: (college: UserCollege) => void;
}>;

const MyCollegeCard = ({
  college,
  hasFamilyIncome,
  onStatusChange,
  onPlanChange,
  onRemove,
}: MyCollegeCardProps) => {
  const dispatch = useAppDispatch();

  return (
    <ImageCard
      id={`my-colleges-card-${college.id}`}
      imageUrl={college.banner_media_url ?? ''}
      fallback={<MeshGradient config={DEFAULT_CONFIG} />}
      imageOverlay={
        <>
          <Box
            className="my-colleges-card-actions"
            sx={actionsSx(canOpenAwardLetter(college) ? null : college.id)}
          >
            <ActionTabs
              actions={[
                {
                  id: `my-colleges-card-remove-${college.id}`,
                  icon: 'Trash01',
                  onClick: () => onRemove(college),
                  tooltipHoverText: STRINGS.remove,
                },
                {
                  id: `my-colleges-card-award-letter-${college.id}`,
                  ...awardLetterAction(college, dispatch),
                },
              ]}
            />
          </Box>
          <Box sx={chipAreaSx}>
            <CollegeChip id={`my-colleges-card-link-${college.id}`} college={college} />
          </Box>
        </>
      }
      sxOverrides={{ container: cardSx, titleDescChildContainer: bodyContainerSx }}
    >
      <Box sx={bodySx}>
        <Box sx={statusLineSx}>
          {hasDeadline(college) && (
            <Typography variant="text-sm-semibold" sx={deadlineSx}>
              {STRINGS.card.deadline(deadlineText(college))}
            </Typography>
          )}
          <Box sx={statusSx}>
            <StatusChip
              id={`my-colleges-card-status-${college.id}`}
              college={college}
              onChange={(status) => onStatusChange(college, status)}
            />
          </Box>
        </Box>

        <Box component="dl" sx={factsSx}>
          <Box sx={factRowSx}>
            <Typography component="dt" variant="text-sm-semibold">
              {STRINGS.card.plan}
            </Typography>
            <Box component="dd" sx={valueSx}>
              <PlanPicker
                id={`my-colleges-card-plan-${college.id}`}
                college={college}
                onChange={(plan) => onPlanChange(college, plan)}
              />
            </Box>
          </Box>
          <Box sx={factRowSx}>
            <Typography component="dt" variant="text-sm-semibold">
              {STRINGS.card.netCost}
            </Typography>
            <Box component="dd" sx={valueSx}>
              <NetCostText college={college} hasFamilyIncome={hasFamilyIncome} />
            </Box>
          </Box>
        </Box>

        <RequirementTags
          id={`my-colleges-card-requirements-${college.id}`}
          college={college}
          long
        />
      </Box>
    </ImageCard>
  );
};

const groupsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
  gap: backrs.spacing['spacing-xl'],
});

const cardSx: SxProps<Theme> = {
  width: 'auto',
  maxWidth: { xs: 'none', sm: 'none' },
  cursor: 'auto',
  userSelect: 'auto',
  '&:hover': { boxShadow: 'none', transform: 'none' },
  '&:hover .my-colleges-card-actions, &:focus-within .my-colleges-card-actions': {
    opacity: 1,
  },
};

const bodyContainerSx: SxProps<Theme> = {
  padding: 0,
};

const actionsSx =
  (disabledAwardLetterId: number | null): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'absolute',
    top: backrs.spacing['spacing-lg'],
    right: backrs.spacing['spacing-lg'],
    opacity: 0,
    transition: 'opacity 0.15s',
    '@media (hover: none)': { opacity: 1 },
    ...(disabledAwardLetterId !== null && {
      [`& #my-colleges-card-award-letter-${disabledAwardLetterId}`]: {
        cursor: 'default',
        '& svg': { color: backrs.colors['Colors/Foreground/fg-disabled-subtle'] },
      },
    }),
  });

const chipAreaSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  left: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
  bottom: backrs.spacing['spacing-lg'],
  display: 'flex',
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
});

const statusLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-sm'],
});

const statusSx: SxProps<Theme> = {
  marginInlineStart: 'auto',
};

const deadlineSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const factsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  margin: 0,
});

const factRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

const valueSx: SxProps<Theme> = {
  margin: 0,
  textAlign: 'right',
};
